import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import '../styles/Login.css';

export default function Login({ setUser, setToken }) {
  const navigate = useNavigate();
  const location = useLocation();
  const infoMessage = location.state?.infoMessage;
  const returnUrl = location.state?.returnUrl || '/';

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [unverifiedEmail, setUnverifiedEmail] = useState('');
  const [resendStatus, setResendStatus] = useState('');

  // ONEID notice modal state
  const [showOneIdNotice, setShowOneIdNotice] = useState(false);

  // Google OAuth state
  const [googleClientId, setGoogleClientId] = useState('');
  const [showGoogleModal, setShowGoogleModal] = useState(false);
  const [customClientId, setCustomClientId] = useState('');
  const [savingClientId, setSavingClientId] = useState(false);

  useEffect(() => {
    axios.get('/api/auth/google-config')
      .then(res => {
        if (res.data?.clientId && res.data.clientId !== 'your_google_client_id') {
          setGoogleClientId(res.data.clientId);
          setCustomClientId(res.data.clientId);
        }
      })
      .catch(() => {});
  }, []);

  // Password reset modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetIdentifier, setResetIdentifier] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState({ type: '', text: '' });

  const handleSendResetCode = async () => {
    if (!resetIdentifier.trim()) {
      setResetMessage({ type: 'error', text: 'Iltimos, Google pochtangizni yoki loginingizni kiriting' });
      return;
    }

    try {
      setSendingCode(true);
      setResetMessage({ type: '', text: '' });
      const res = await axios.post('/api/auth/send-reset-code', {
        email: resetIdentifier
      });
      setCodeSent(true);
      setResetMessage({
        type: 'success',
        text: res.data.message || '✓ Tasdiqlash kodi elektron pochtangizga muvaffaqiyatli yuborildi!'
      });
    } catch (err) {
      setResetMessage({
        type: 'error',
        text: err.response?.data?.error || 'Kodni yuborishda xatolik yuz berdi'
      });
    } finally {
      setSendingCode(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!resetIdentifier.trim() || !resetNewPassword.trim()) {
      setResetMessage({ type: 'error', text: 'Login/elektron pochta va yangi parolni kiriting' });
      return;
    }
    if (codeSent && !resetCode.trim()) {
      setResetMessage({ type: 'error', text: 'Iltimos, pochtangizga yuborilgan 6 xonali tasdiqlash kodini kiriting' });
      return;
    }
    if (resetNewPassword !== resetConfirmPassword) {
      setResetMessage({ type: 'error', text: 'Kiritilgan yangi parollar bir-biriga mos kelmadi' });
      return;
    }

    try {
      setResetLoading(true);
      setResetMessage({ type: '', text: '' });
      const res = await axios.post('/api/auth/reset-password', {
        identifier: resetIdentifier,
        newPassword: resetNewPassword,
        code: resetCode.trim()
      });

      setResetMessage({ type: 'success', text: res.data.message });
      setIdentifier(resetIdentifier);
      setPassword(resetNewPassword);
      setTimeout(() => {
        setShowResetModal(false);
        setResetIdentifier('');
        setResetCode('');
        setCodeSent(false);
        setResetNewPassword('');
        setResetConfirmPassword('');
        setResetMessage({ type: '', text: '' });
      }, 2000);
    } catch (err) {
      setResetMessage({
        type: 'error',
        text: err.response?.data?.error || 'Parolni tiklashda xatolik yuz berdi'
      });
    } finally {
      setResetLoading(false);
    }
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) {
      setError('Iltimos, login yoki elektron pochta manzilingizni kiriting');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const response = await axios.post('/api/auth/login', {
        email: identifier,
        password: password
      });

      const { token, user } = response.data;
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      if (setToken) setToken(token);
      if (setUser) setUser(user);

      if (user.role === 'admin') {
        navigate('/admin');
      } else if (returnUrl && returnUrl !== '/') {
        navigate(returnUrl);
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.response?.data?.unverified) {
        setUnverifiedEmail(err.response.data.email || identifier);
      } else {
        setUnverifiedEmail('');
      }
      setError(err.response?.data?.error || 'Kirishda xatolik yuz berdi. Login yoki parolni tekshiring.');
    } finally {
      setLoading(false);
    }
  };

  const handleOneIdClick = () => {
    setShowOneIdNotice(true);
  };

  const triggerGoogleOAuth = (clientIdToUse) => {
    const activeClientId = clientIdToUse || googleClientId;
    if (!window.google?.accounts?.oauth2) {
      setError('Google xizmati kutubxonasi hali toʻliq yuklanmadi. Iltimos, sahifani qayta yuklang.');
      return;
    }

    try {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: activeClientId,
        scope: 'https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email openid',
        callback: async (tokenResponse) => {
          if (tokenResponse.error) {
            console.error('Google OAuth error:', tokenResponse);
            if (tokenResponse.error !== 'popup_closed_by_user') {
              setError(`Google xatosi: ${tokenResponse.error_description || tokenResponse.error}`);
            }
            return;
          }

          try {
            setLoading(true);
            setError('');
            // Haqiqiy Google foydalanuvchi maʼlumotlarini Google rasmiy API sidan olamiz
            const userInfoRes = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
              headers: { Authorization: `Bearer ${tokenResponse.access_token}` }
            });

            const { email, name, picture, sub } = userInfoRes.data;

            // Haqiqiy Google profili (email, ism, rasm) ni serverga yuboramiz
            const res = await axios.post('/api/auth/google', {
              email,
              name,
              avatar: picture,
              googleId: sub
            });

            const { token, user } = res.data;
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            if (setToken) setToken(token);
            if (setUser) setUser(user);

            if (user.role === 'admin') {
              navigate('/admin');
            } else if (returnUrl && returnUrl !== '/' && returnUrl !== '/admin') {
              navigate(returnUrl);
            } else {
              navigate('/');
            }
          } catch (err) {
            console.error('Google profile fetch error:', err);
            setError('Google akkauntingiz maʼlumotlarini yuklashda xatolik yuz berdi');
          } finally {
            setLoading(false);
          }
        }
      });

      client.requestAccessToken({ prompt: 'select_account' });
    } catch (err) {
      console.error('initTokenClient error:', err);
      setError('Google tizimiga ulanishda xatolik yuz berdi. Client ID toʻgʻriligini tekshiring.');
    }
  };

  const handleGoogleLogin = () => {
    if (googleClientId && googleClientId !== 'your_google_client_id') {
      triggerGoogleOAuth(googleClientId);
    } else {
      setShowGoogleModal(true);
    }
  };

  const handleSaveGoogleClientId = async (e) => {
    e.preventDefault();
    if (!customClientId.trim()) return;
    try {
      setSavingClientId(true);
      const res = await axios.post('/api/auth/set-google-client-id', { clientId: customClientId.trim() });
      setGoogleClientId(res.data.clientId);
      setShowGoogleModal(false);
      triggerGoogleOAuth(res.data.clientId);
    } catch (err) {
      alert('Client ID saqlashda xatolik: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingClientId(false);
    }
  };

  return (
    <div className="login-page-wrapper">
      <div className="login-card-container">
        {/* Turon universiteti rasmiy logotipi (Image 3) */}
        <div className="university-brand-header" onClick={() => navigate('/')} title="Bosh sahifaga oʻtish">
          <img src="/logo.png" alt="Turon University" className="turon-auth-logo" />
        </div>

        {/* Asosiy kirish oynasi (Image 3) */}
        <div className="auth-box-card">
          <h2 className="auth-card-title">Hisobingizga kiring</h2>

          {infoMessage && !error && (
            <div className="auth-alert-info">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
              <span>{infoMessage}</span>
            </div>
          )}

          {error && (
            <div className="auth-alert-error">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
              <span>{error}</span>
            </div>
          )}

          {unverifiedEmail && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: '#991b1b' }}>
              <p style={{ margin: '0 0 8px 0', fontWeight: '600' }}>
                Elektron poçtangiz hali tasdiqlanmagan ({unverifiedEmail}).
              </p>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                <Link to={`/verify-email`} style={{ color: '#1e40af', fontWeight: '700', textDecoration: 'underline' }}>
                  Kodni kiritish va faollaştiriş →
                </Link>
                <span>•</span>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      setResendStatus('Yuborilmoqda...');
                      const res = await axios.post('/api/auth/resend-verification', { email: unverifiedEmail });
                      setResendStatus(res.data.message || 'Yangi tasdiqlaş xati yuborildi!');
                    } catch (e) {
                      setResendStatus('Xat yuborişda xatolik yuz berdi');
                    }
                  }}
                  style={{ background: 'none', border: 'none', color: '#dc2626', fontWeight: '700', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                >
                  Xatni qayta yuboriş
                </button>
              </div>
              {resendStatus && <div style={{ marginTop: '6px', color: '#15803d', fontWeight: '600' }}>✓ {resendStatus}</div>}
            </div>
          )}

          <form onSubmit={handleLoginSubmit} className="auth-login-form">
            <div className="input-field-group">
              <label htmlFor="user-identifier">Login yoki Email</label>
              <input
                id="user-identifier"
                type="text"
                placeholder="Login"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                disabled={loading}
                autoFocus
              />
            </div>

            <div className="input-field-group">
              <div className="password-label-row">
                <label htmlFor="user-password">Parol</label>
                <button
                  type="button"
                  className="forgot-pass-btn"
                  onClick={() => {
                    setShowResetModal(true);
                    setResetMessage({ type: '', text: '' });
                  }}
                >
                  Parolni unutdim
                </button>
              </div>

              <div className="password-input-wrapper">
                <input
                  id="user-password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Parol"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                />
                <button
                  type="button"
                  className="eye-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex="-1"
                  aria-label="Parolni koʻrsatish"
                >
                  {showPassword ? (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                  ) : (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#64748b" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                  )}
                </button>
              </div>
            </div>

            <div className="checkbox-remember-row">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <span className="checkmark-box"></span>
                <span className="remember-label-text">Bu qurilmada meni eslab qoling</span>
              </label>
            </div>

            <button type="submit" className="btn-main-submit" disabled={loading}>
              {loading ? 'Tekshirilmoqda...' : 'Kirish'}
            </button>
          </form>

          <div className="yoki-divider">
            <span>YOKI</span>
          </div>

          {/* OneID va Google orqali avtorizatsiya */}
          <div className="sso-auth-buttons">
            <button
              type="button"
              className="sso-btn oneid-btn"
              onClick={handleOneIdClick}
              disabled={loading}
              title="OneID orqali kirish"
            >
              <img src="/oneid-logo.svg" alt="OneID" className="oneid-btn-logo" />
              <span>OneID</span>
            </button>

            <button type="button" className="sso-btn google-btn" onClick={handleGoogleLogin} disabled={loading} title="Google orqali kirish">
              <svg width="18" height="18" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
              </svg>
              <span>Google</span>
            </button>
          </div>
        </div>

        {/* Roʻyxatdan oʻtish havolasi (Image 3: Hali hisobingiz yoʻqmi? Roʻyxatdan oʻtish) */}
        <div className="auth-footer-prompt">
          <span>Hali hisobingiz yoʻqmi? </span>
          <Link to="/register-account" className="register-now-link">Roʻyxatdan oʻtish</Link>
        </div>
      </div>

      {/* ONEID rasmiy axborot modali */}
      {showOneIdNotice && (
        <div className="reset-modal-overlay" onClick={() => setShowOneIdNotice(false)}>
          <div className="oneid-notice-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="oneid-notice-header">
              <div className="oneid-badge-wrap">
                <img src="/oneid-logo.svg" alt="ONEID Logo" className="oneid-modal-top-logo" />
                <span className="oneid-notice-pill">Rasmiy axborot</span>
              </div>
              <button
                type="button"
                className="btn-close-reset-modal"
                onClick={() => setShowOneIdNotice(false)}
              >
                ✕
              </button>
            </div>

            <div className="oneid-notice-content">
              <div className="oneid-info-alert">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#003399" strokeWidth="2.2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                <span>Hozirda tizim integratsiyasi jarayoni davom etmoqda</span>
              </div>

              <h3 className="oneid-notice-title">Yagona identifikatsiya tizimi (ONEID) orqali kirish</h3>

              <div className="oneid-notice-body">
                <p>
                  Hurmatli talabgor! Turon universiteti hamda Raqamli texnologiyalar vazirligi huzuridagi <strong>Yagona identifikatsiya tizimi (ONEID — id.egov.uz / UZINFOCOM)</strong> oʻrtasida rasmiy integratsiya shartnomasi va axborot xavfsizligi protokollarini oʻrnatish boʻyicha amaliy ishlar olib borilmoqda.
                </p>
                <p>
                  Shartnoma va texnik ulanish yakunlangach, ONEID orqali pasport/ID-karta yoki E-imzo yordamida avtomatik kirish toʻliq ishga tushiriladi.
                </p>
                <div className="oneid-alt-ways">
                  <h4>Hozirgi vaqtda tizimga kirish usullari:</h4>
                  <ul>
                    <li>Universitet tomonidan taqdim etilgan <strong>Login va parol</strong> orqali</li>
                    <li>Oʻzingizning <strong>Google hisobingiz</strong> orqali bir zumda</li>
                    <li>Yoki <strong>Roʻyxatdan oʻtish</strong> boʻlimi orqali yangi hisob yaratish</li>
                  </ul>
                </div>
              </div>

              <div className="oneid-notice-actions">
                <button
                  type="button"
                  className="btn-modal-google"
                  onClick={() => {
                    setShowOneIdNotice(false);
                    handleGoogleLogin();
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                  </svg>
                  <span>Google</span>
                </button>
                <button
                  type="button"
                  className="btn-modal-dismiss"
                  onClick={() => setShowOneIdNotice(false)}
                >
                  Tushundim
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GOOGLE CLIENT ID SOZLASH VA AXBOROT MODALI */}
      {showGoogleModal && (
        <div className="reset-modal-overlay" onClick={() => setShowGoogleModal(false)}>
          <div className="oneid-notice-modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '540px' }}>
            <div className="oneid-notice-header">
              <div className="oneid-badge-wrap">
                <svg width="22" height="22" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" />
                </svg>
                <span className="oneid-notice-pill" style={{ background: '#e0f2fe', color: '#0369a1' }}>Google OAuth 2.0</span>
              </div>
              <button
                type="button"
                className="btn-close-reset-modal"
                onClick={() => setShowGoogleModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="oneid-notice-content">
              <h3 className="oneid-notice-title" style={{ marginTop: '8px' }}>Google orqali haqiqiy kirishni ulash</h3>

              <div className="oneid-notice-body" style={{ fontSize: '13.5px', lineHeight: '1.6' }}>
                <p>
                  Google xavfsizlik standarti (OAuth 2.0) boʻyicha, portalga foydalanuvchining haqiqiy Google profili, ismi va rasmini olish uchun <strong>Google Client ID</strong> kiritiladi.
                </p>
                <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0', margin: '12px 0' }}>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#0f2b5c' }}>Qanday olinadi (bepul, 2 daqiqa):</h4>
                  <ol style={{ margin: 0, paddingLeft: '18px', color: '#475569', fontSize: '12.5px', lineHeight: '1.8' }}>
                    <li><strong>console.cloud.google.com/apis/credentials</strong> ga kiring</li>
                    <li><strong>Create Credentials &gt; OAuth client ID</strong> ni bosing</li>
                    <li>Application type: <strong>Web application</strong></li>
                    <li>Authorized JavaScript origins ga: <strong>https://turonservices.duckdns.org</strong> ni qoʻshing</li>
                    <li>Olingan Client ID ni nusxalab quyidagi maydonga joylang:</li>
                  </ol>
                </div>

                <form onSubmit={handleSaveGoogleClientId} style={{ marginTop: '14px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: '700', color: '#334155', marginBottom: '6px' }}>
                    Google Client ID ni kiriting:
                  </label>
                  <input
                    type="text"
                    placeholder="Masalan: 1234567890-abcdefgh.apps.googleusercontent.com"
                    value={customClientId}
                    onChange={(e) => setCustomClientId(e.target.value)}
                    style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', boxSizing: 'border-box', marginBottom: '14px' }}
                    required
                  />
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      className="btn-modal-dismiss"
                      onClick={() => setShowGoogleModal(false)}
                    >
                      Bekor qilish
                    </button>
                    <button
                      type="submit"
                      disabled={savingClientId || !customClientId.trim()}
                      style={{ background: '#0284c7', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontWeight: '700', cursor: 'pointer', fontSize: '13px' }}
                    >
                      {savingClientId ? 'Saqlanmoqda...' : 'Saqlash va Google orqali kirish'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Parolni qayta tiklash modali (Mehmon profili & Google pochta uchun) */}
      {showResetModal && (
        <div className="reset-modal-overlay" onClick={() => setShowResetModal(false)}>
          <div className="reset-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="reset-modal-header">
              <div className="header-icon-title">
                <span className="reset-key-icon">🔑</span>
                <h3>Parolni qayta tiklash</h3>
              </div>
              <button
                type="button"
                className="btn-close-reset-modal"
                onClick={() => setShowResetModal(false)}
              >
                ✕
              </button>
            </div>

            <p className="reset-modal-desc">
              Turon universiteti portalidagi loginingiz yoki Google elektron pochtangizni kiriting va hisobingiz uchun yangi parol belgilang.
            </p>

            {resetMessage.text && (
              <div className={`reset-alert-box ${resetMessage.type}`}>
                {resetMessage.type === 'success' ? '✓ ' : '⚠️ '}
                {resetMessage.text}
              </div>
            )}

            <form onSubmit={handleResetSubmit} className="reset-modal-form">
              <div className="input-field-group">
                <label>Login yoki Google elektron pochta:</label>
                <div className="reset-email-action-row">
                  <input
                    type="text"
                    placeholder="Masalan: oybek.asrorov@gmail.com yoki login"
                    value={resetIdentifier}
                    onChange={(e) => setResetIdentifier(e.target.value)}
                    disabled={resetLoading}
                    required
                  />
                  <button
                    type="button"
                    className="btn-send-reset-code"
                    onClick={handleSendResetCode}
                    disabled={sendingCode || !resetIdentifier.trim()}
                    title="Google pochtaga 6 xonali tasdiqlash kodi yuborish"
                  >
                    {sendingCode ? '...' : 'Kod yuborish ✉️'}
                  </button>
                </div>
              </div>

              {codeSent && (
                <div className="input-field-group">
                  <label>Pochtaga kelgan tasdiqlash kodi:</label>
                  <input
                    type="text"
                    placeholder="6 xonali kod (masalan: 123456)"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                  />
                </div>
              )}

              <div className="input-field-group">
                <label>Yangi parol:</label>
                <input
                  type="password"
                  placeholder="Kamida 4 ta belgi"
                  value={resetNewPassword}
                  onChange={(e) => setResetNewPassword(e.target.value)}
                  disabled={resetLoading}
                  required
                />
              </div>

              <div className="input-field-group">
                <label>Yangi parolni tasdiqlash:</label>
                <input
                  type="password"
                  placeholder="Yangi parolni qayta kiriting"
                  value={resetConfirmPassword}
                  onChange={(e) => setResetConfirmPassword(e.target.value)}
                  disabled={resetLoading}
                  required
                />
              </div>

              <div className="reset-modal-actions">
                <button
                  type="button"
                  className="btn-reset-cancel"
                  onClick={() => setShowResetModal(false)}
                >
                  Bekor qilish
                </button>
                <button
                  type="submit"
                  className="btn-reset-submit"
                  disabled={resetLoading}
                >
                  {resetLoading ? 'Tekshirilmoqda...' : 'Parolni yangilash'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
